import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, FileText, Info, Upload, Trash2, Pencil, FolderTree, Plus, X,
} from 'lucide-react';
import { apiFetch, API_BASE, handleUnauthorized } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Button } from '@/components/Button';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { FilePickerField } from '@/components/FilePickerField';
import { DOCUMENT_ACCEPT, DOCUMENT_FORMAT_HINT } from '@/lib/document-formats';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useLabel } from '@/lib/labels';
// ⚠️⚠️ design-and-code H-1: `GET /condition-sets` のレスポンスキーは `sets` (routes/condition-sets.ts:163)。
//  当初 `condition_sets` という**存在しないキー**を自前の型で宣言していたため、セレクタが恒久的に空になり
//  **Full 版の中核 (D-431 条件による公開範囲) が UI から到達不能**だった。型を自分で宣言すると
//  tsc は「嘘の型」を検証してしまうため、**API と共有されている既存の型を使う**。
import type { ConditionSetsResponse } from '@/components/conditions/types';

/**
 * `/documents` (v0.4 追加要件⑰): 社内文書ライブラリの一覧 + 管理。
 * 設計: docs/design/v04-document-library-detail.md §6-1
 *
 * ⚠️ 1画面2モード: 全員が一覧を見て、`document:manage` 保有者にだけ追加・改定・編集・削除が出る。
 *    管理専用画面を分けると nav が1項目増え、⑱ の再編直後にグループを膨らませることになるため。
 * ⚠️ 「ダウンロードを禁止しています」と断言しない (D-433)。ブラウザ内蔵 PDF ビューアの保存ボタンや
 *    スクリーンショットは原理的に抑止できず、断言すると利用者に**誤った安心**を与える。
 */

export type DocumentSummary = {
  document_id: string;
  title: string;
  description: string | null;
  // ⚠️ 使い勝手フィードバック (2026-09-04、D-U1/D-U2): 自由入力の category は非推奨。
  // 以後は category_id (document_categories 参照) のみを書込む。category はレガシー表示用に残す。
  category: string | null;
  category_id: string | null;
  sort_order: number;
  // ⚠️ security M-1: manage 保有者にだけ返る (一般従業員には「誰に何を見せていないか」を出さない)
  visibility_condition_set_id?: string | null;
  visibility_status?: 'all' | 'limited' | 'empty' | 'broken';
  download_allowed: boolean;
  current_version_no: number | null;
  content_type: string | null;
  inline_viewable: boolean;
  status: 'active' | 'archived';
  updated_at: string;
};

type ListResponse = {
  can_manage: boolean;
  supports_visibility_conditions: boolean;
  documents: DocumentSummary[];
};

type DocCategory = { category_id: string; parent_category_id: string | null; name: string; sort_order: number };
type CategoriesResponse = { categories: DocCategory[] };

/** D-U1: サブカテゴリ (ネスト) を持つフラットな一覧から、深さ優先・sort_order順の
 *  表示用リストを組み立てる (`lib/node-tree.ts` の flattenNodes と同じ思想、対象データが
 *  ノードツリーではなく文書カテゴリ専用のためこのファイルに閉じる)。 */
const buildCategoryList = (categories: DocCategory[]): Array<{ category: DocCategory; depth: number }> => {
  const byParent = new Map<string | null, DocCategory[]>();
  for (const c of categories) {
    const key = c.parent_category_id;
    const list = byParent.get(key);
    if (list) list.push(c); else byParent.set(key, [c]);
  }
  for (const list of byParent.values()) list.sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name, 'ja'));
  const out: Array<{ category: DocCategory; depth: number }> = [];
  const walk = (parentId: string | null, depth: number) => {
    for (const c of byParent.get(parentId) ?? []) {
      out.push({ category: c, depth });
      walk(c.category_id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
};

const UNCATEGORIZED_ID = '__uncategorized__';

const ALL_ID = '__all__';

/** ⚠️ Lite では条件エンジンが使えず「全員に公開」しか選べない (D-482)。
 *  絞ったつもりで全員に見える事故を防ぐため、**選べないことを画面で明示する**。 */
function VisibilityField({
  supported, value, onChange, options, status,
}: {
  supported: boolean;
  value: string | null;
  onChange: (v: string | null) => void;
  options: { setId: string; label: string }[];
  /** 選択中の条件セットの実効状態。⚠️ 'empty' は「絞ったつもりで全員に見える」状態 */
  status?: 'all' | 'limited' | 'empty' | 'broken';
}) {
  if (!supported) {
    return (
      <div>
        <span className="text-caption text-muted">公開範囲</span>
        <p className="mt-1 flex items-start gap-2 text-body">
          <Info className="w-4 h-4 mt-0.5 shrink-0 text-chrome-base" aria-hidden />
          <span>この文書は<strong>全員に公開</strong>されます (公開範囲の絞り込みは Full 版の機能です)。</span>
        </p>
      </div>
    );
  }
  return (
    <label className="block">
      <span className="text-caption text-muted">公開範囲</span>
      <Select value={value ?? ALL_ID} onValueChange={(v) => onChange(v === ALL_ID ? null : v)}>
        <SelectTrigger className="mt-1 min-h-[44px]" aria-label="公開範囲"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_ID}>全員に公開</SelectItem>
          {options.map((o) => (
            <SelectItem key={o.setId} value={o.setId}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {/* ⚠️ design-expert H-1: 「条件セットが紐づいている」と「実際に絞れている」は別。
          紐づけた後に条件を全削除されると実質全員公開になるため、その状態を常時警告する。 */}
      {status === 'empty' && (
        <p className="mt-1 flex items-start gap-2 text-body text-alert-text">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
          <span>この条件セットは条件が0件のため、<strong>全員に公開されます</strong>。</span>
        </p>
      )}
      {status === 'broken' && (
        <p className="mt-1 flex items-start gap-2 text-body text-alert-text">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
          <span>参照している条件セットが見つかりません。この文書は<strong>誰にも表示されません</strong>。</span>
        </p>
      )}
      <span className="mt-1 block text-body text-text-sub">
        条件はコンフィグの「条件セット」で作成します。条件が0件のセットは全員に公開されるため選べません。
      </span>
    </label>
  );
}

/** D-U2: 文書作成/編集時にカテゴリを選ぶ (現状の延長)。サブカテゴリはインデントで表す。 */
function CategorySelectField({
  categoryList, value, onChange,
}: { categoryList: Array<{ category: DocCategory; depth: number }>; value: string | null; onChange: (v: string | null) => void }) {
  return (
    <label className="block">
      <span className="text-caption text-muted">カテゴリ</span>
      <Select value={value ?? UNCATEGORIZED_ID} onValueChange={(v) => onChange(v === UNCATEGORIZED_ID ? null : v)}>
        <SelectTrigger className="mt-1 min-h-[44px]" aria-label="カテゴリ"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={UNCATEGORIZED_ID}>未分類</SelectItem>
          {categoryList.map(({ category, depth }) => (
            <SelectItem key={category.category_id} value={category.category_id}>
              {'　'.repeat(depth)}{category.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </label>
  );
}

/** D-U1/D-U2: カテゴリマスタの管理 (一括管理UI・ドラッグ&ドロップ並び替えは不要、個別CRUD + 手動の
 *  並び順入力のみ)。document:manage 保有者のみが開ける導線に置く。
 *  ⚠️ 削除確認 (ConfirmDialog) はこのモーダルの中に入れ子にせず、呼び出し元 (DocumentsPage) の
 *  トップレベルに他の ConfirmDialog と並べて置く (Modal.tsx は既定 z-50 を共有するため、
 *  同一階層の入れ子だと重なり順の保証が既存パターンから外れる)。 */
function CategoryManagerModal({
  open, onClose, categoryList, canManage, onRequestDelete,
}: {
  open: boolean; onClose: () => void; categoryList: Array<{ category: DocCategory; depth: number }>;
  canManage: boolean; onRequestDelete: (c: DocCategory) => void;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<DocCategory | null>(null);
  const [draft, setDraft] = useState({ name: '', parentCategoryId: null as string | null, sortOrder: '0' });

  const startCreate = () => { setEditing(null); setDraft({ name: '', parentCategoryId: null, sortOrder: '0' }); };
  const startEdit = (c: DocCategory) => {
    setEditing(c);
    setDraft({ name: c.name, parentCategoryId: c.parent_category_id, sortOrder: String(c.sort_order) });
  };

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['document-categories'] });
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: draft.name.trim(),
        parent_category_id: draft.parentCategoryId,
        sort_order: Number(draft.sortOrder) || 0,
      };
      return editing
        ? apiFetch(`/documents/categories/${editing.category_id}`, { method: 'PUT', body: JSON.stringify(body) })
        : apiFetch('/documents/categories', { method: 'POST', body: JSON.stringify(body) });
    },
    onSuccess: () => { toast.success('カテゴリを保存しました'); startCreate(); invalidate(); },
    onError: (e: Error) => toast.error(e.message || '保存に失敗しました'),
  });

  return (
    <Modal open={open} onClose={onClose} title="カテゴリを管理">
      <div className="space-y-4">
        {categoryList.length === 0 ? (
          <p className="text-body text-text-sub">カテゴリはまだありません。</p>
        ) : (
          <ul className="divide-y divide-surface-border border border-surface-border rounded-lg">
            {categoryList.map(({ category, depth }) => (
              <li key={category.category_id} className="flex items-center justify-between gap-2 px-3 py-2">
                <span className="text-body break-words min-w-0">{'　'.repeat(depth)}{category.name}</span>
                {canManage && (
                  <div className="flex gap-1 shrink-0">
                    <Button variant="secondary" size="sm" onClick={() => startEdit(category)} aria-label={`${category.name}を編集`} className="min-h-[44px] min-w-[44px]">
                      <Pencil className="w-4 h-4" aria-hidden />
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => onRequestDelete(category)} aria-label={`${category.name}を削除`} className="min-h-[44px] min-w-[44px]">
                      <Trash2 className="w-4 h-4" aria-hidden />
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {canManage && (
          <div className="border border-surface-border rounded-lg p-3 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-body font-medium text-text-main">{editing ? `「${editing.name}」を編集` : 'カテゴリを追加'}</h3>
              {editing && (
                <Button variant="secondary" size="sm" onClick={startCreate} aria-label="編集をやめて新規作成に戻る"><X className="w-4 h-4" aria-hidden /></Button>
              )}
            </div>
            <label className="block">
              <span className="text-caption text-muted">名前<span className="text-alert-text ml-1">*</span></span>
              <input className="mt-1 w-full min-h-[44px] border border-surface-border rounded px-3"
                value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </label>
            <label className="block">
              <span className="text-caption text-muted">親カテゴリ</span>
              <Select value={draft.parentCategoryId ?? UNCATEGORIZED_ID}
                onValueChange={(v) => setDraft({ ...draft, parentCategoryId: v === UNCATEGORIZED_ID ? null : v })}>
                <SelectTrigger className="mt-1 min-h-[44px]" aria-label="親カテゴリ"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNCATEGORIZED_ID}>なし (トップレベル)</SelectItem>
                  {categoryList
                    .filter(({ category }) => category.category_id !== editing?.category_id)
                    .map(({ category, depth }) => (
                      <SelectItem key={category.category_id} value={category.category_id}>{'　'.repeat(depth)}{category.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </label>
            <label className="block">
              <span className="text-caption text-muted">並び順 (小さいほど先頭)</span>
              <input type="number" className="mt-1 w-full min-h-[44px] border border-surface-border rounded px-3"
                value={draft.sortOrder} onChange={(e) => setDraft({ ...draft, sortOrder: e.target.value })} />
            </label>
            <div className="flex justify-end">
              <Button leftIcon={editing ? <Pencil className="w-4 h-4" aria-hidden /> : <Plus className="w-4 h-4" aria-hidden />}
                disabled={!draft.name.trim() || save.isPending} onClick={() => save.mutate()}>
                {editing ? '保存' : '追加'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default function DocumentsPage() {
  const t = useLabel();
  const qc = useQueryClient();
  const [pickedFile, setPickedFile] = useState<File | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState<DocumentSummary | null>(null);
  const [deleting, setDeleting] = useState<DocumentSummary | null>(null);
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState<DocCategory | null>(null);
  const [form, setForm] = useState({
    title: '', description: '', categoryId: null as string | null, sortOrder: '0', downloadAllowed: false,
    conditionSetId: null as string | null,
  });

  const { data, isLoading, isError } = useQuery<ListResponse>({
    queryKey: ['documents'],
    queryFn: () => apiFetch<ListResponse>('/documents'),
  });

  const { data: categoriesData } = useQuery<CategoriesResponse>({
    queryKey: ['document-categories'],
    queryFn: () => apiFetch<CategoriesResponse>('/documents/categories'),
  });
  const categoryList = useMemo(() => buildCategoryList(categoriesData?.categories ?? []), [categoriesData]);

  const canManage = data?.can_manage ?? false;
  const supportsConditions = data?.supports_visibility_conditions ?? false;

  const { data: conditionSets } = useQuery<ConditionSetsResponse>({
    queryKey: ['condition-sets', 'document_visibility'],
    queryFn: () => apiFetch<ConditionSetsResponse>('/condition-sets?usage_point_id=document_visibility'),
    enabled: canManage && supportsConditions,
  });
  const conditionSetOptions = (conditionSets?.sets ?? [])
    .map((cs) => ({ setId: cs.set_id, label: cs.label ?? cs.set_id }));

  const resetForm = () => setForm({
    title: '', description: '', categoryId: null, sortOrder: '0', downloadAllowed: false, conditionSetId: null,
  });

  const upload = useMutation({
    mutationFn: async () => {
      if (!pickedFile) throw new Error('ファイルを選択してください');
      const fd = new FormData();
      fd.set('title', form.title);
      if (form.description) fd.set('description', form.description);
      if (form.categoryId) fd.set('category_id', form.categoryId);
      if (form.sortOrder) fd.set('sort_order', form.sortOrder);
      fd.set('download_allowed', String(form.downloadAllowed));
      if (form.conditionSetId) fd.set('visibility_condition_set_id', form.conditionSetId);
      fd.set('file', pickedFile);
      // ⚠️ apiFetch は Content-Type を application/json に固定するため multipart には使えない
      const res = await fetch(`${API_BASE}/documents`, {
        method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('access_token') ?? ''}` }, body: fd,
      });
      handleUnauthorized(res.status);
      const body = await res.json() as { success: boolean; error?: { message: string } };
      if (!res.ok || !body.success) throw new Error(body.error?.message ?? 'アップロードに失敗しました');
    },
    onSuccess: () => {
      toast.success('文書を登録しました');
      setUploadOpen(false);
      resetForm();
      setPickedFile(null);
      void qc.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const update = useMutation({
    mutationFn: (d: DocumentSummary) => apiFetch(`/documents/${d.document_id}`, {
      method: 'PUT',
      body: JSON.stringify({
        title: form.title,
        description: form.description || null,
        category_id: form.categoryId,
        sort_order: Number(form.sortOrder) || 0,
        download_allowed: form.downloadAllowed,
        ...(supportsConditions ? { visibility_condition_set_id: form.conditionSetId } : {}),
      }),
    }),
    onSuccess: () => {
      toast.success('文書を更新しました');
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const archive = useMutation({
    mutationFn: (d: DocumentSummary) => apiFetch(`/documents/${d.document_id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('文書をアーカイブしました');
      setDeleting(null);
      void qc.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const removeCategory = useMutation({
    mutationFn: (cat: DocCategory) => apiFetch(`/documents/categories/${cat.category_id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('カテゴリを削除しました');
      setDeletingCategory(null);
      void qc.invalidateQueries({ queryKey: ['document-categories'] });
    },
    onError: (e: Error) => { toast.error(e.message || '削除に失敗しました'); setDeletingCategory(null); },
  });

  const openEdit = (d: DocumentSummary) => {
    setForm({
      title: d.title, description: d.description ?? '', categoryId: d.category_id, sortOrder: String(d.sort_order),
      downloadAllowed: d.download_allowed, conditionSetId: d.visibility_condition_set_id ?? null,
    });
    setEditing(d);
  };

  /** 編集中の文書の実効状態。追加モーダルでは未保存のため 'limited' 扱いでよい (選べるのは非空のみ) */
  const editingStatus = editing?.visibility_status;

  const rows = useMemo(() => data?.documents ?? [], [data]);

  // D-U1: フラットな一覧をやめ、カテゴリごとにグルーピングして表示する。
  // セクションの並びはカテゴリツリーの表示順に従い、未分類は末尾に固定する。
  const groupedRows = useMemo(() => {
    const byCategory = new Map<string, DocumentSummary[]>();
    for (const d of rows) {
      const key = d.category_id ?? UNCATEGORIZED_ID;
      const list = byCategory.get(key);
      if (list) list.push(d); else byCategory.set(key, [d]);
    }
    const sections: Array<{ key: string; label: string; documents: DocumentSummary[] }> = [];
    for (const { category } of categoryList) {
      const documents = byCategory.get(category.category_id);
      if (documents) sections.push({ key: category.category_id, label: category.name, documents });
    }
    const uncategorized = byCategory.get(UNCATEGORIZED_ID);
    if (uncategorized) sections.push({ key: UNCATEGORIZED_ID, label: '未分類', documents: uncategorized });
    return sections;
  }, [rows, categoryList]);

  const renderDocumentItem = (d: DocumentSummary) => (
    <li key={d.document_id} className="border border-surface-border rounded-lg bg-white p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          {/* ⚠️ このリンクが**モバイルで詳細画面へ入る唯一の導線**なので、
              インラインの文字リンクのままにせず 44px のタップ領域を確保する
              (短いタイトルだと 19px しかなかった)。 */}
          <Link to={`/documents/${d.document_id}`}
            className="inline-flex items-center min-h-[44px] text-body font-medium text-chrome-base underline break-words">
            <FileText className="w-4 h-4 inline mr-1.5 -mt-0.5" aria-hidden />{d.title}
          </Link>
          {d.description && <p className="mt-1 text-body text-text-sub break-words">{d.description}</p>}
          <p className="mt-1 text-caption text-text-sub">
            {d.current_version_no !== null && <span className="mr-3">第{d.current_version_no}版</span>}
            <span>更新 {d.updated_at.slice(0, 10)}</span>
          </p>
          {canManage && (
            <>
            <p className="mt-1 text-body text-text-sub">
              公開範囲: {
                d.visibility_status === 'all' ? '全員'
                  : d.visibility_status === 'limited' ? '条件で限定'
                    : d.visibility_status === 'empty' ? '条件0件のため全員に公開'
                      : '条件セットが見つかりません'
              }
              {d.status === 'archived' && <span className="ml-3">アーカイブ済み</span>}
            </p>
            {/* ⚠️ 「条件で限定したつもりが全員に見えている」を一覧でも気づけるようにする */}
            {(d.visibility_status === 'empty' || d.visibility_status === 'broken') && (
              <p className="mt-1 flex items-start gap-2 text-body text-alert-text">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
                <span>
                  {d.visibility_status === 'empty'
                    ? '公開範囲の条件が0件です。全員に公開されています。'
                    : '公開範囲の条件セットが見つからないため、誰にも表示されていません。'}
                </span>
              </p>
            )}
            </>
          )}
        </div>
        {canManage && (
          <div className="flex gap-2 shrink-0">
            <Button variant="secondary" onClick={() => openEdit(d)} aria-label={`${d.title} を編集`} className="min-h-[44px] min-w-[44px]">
              <Pencil className="w-4 h-4" aria-hidden />
            </Button>
            {d.status === 'active' && (
              <Button variant="secondary" onClick={() => setDeleting(d)} aria-label={`${d.title} をアーカイブ`} className="min-h-[44px] min-w-[44px]">
                <Trash2 className="w-4 h-4" aria-hidden />
              </Button>
            )}
          </div>
        )}
      </div>
    </li>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-h1">{t('entity.documents', '社内文書')}</h1>
          <p className="mt-1 text-body text-text-sub">就業規則・各種規程などを閲覧できます。</p>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setCategoryManagerOpen(true)}>
              <FolderTree className="w-4 h-4 mr-1.5" aria-hidden />カテゴリを管理
            </Button>
            <Button onClick={() => { resetForm(); setUploadOpen(true); }}>
              <Upload className="w-4 h-4 mr-1.5" aria-hidden />文書を追加
            </Button>
          </div>
        )}
      </div>

      {isLoading && <p className="text-body text-muted">読み込み中…</p>}
      {isError && <p className="text-body text-alert-text">文書の取得に失敗しました。時間をおいて再度お試しください。</p>}

      {!isLoading && !isError && rows.length === 0 && (
        <p className="text-body text-text-sub">閲覧できる文書はありません。</p>
      )}

      {groupedRows.length > 0 && (
        <div className="space-y-6">
          {groupedRows.map((section) => (
            <section key={section.key} className="space-y-2">
              <h2 className="text-section text-text-section">{section.label}</h2>
              <ul className="space-y-2">
                {section.documents.map((d) => renderDocumentItem(d))}
              </ul>
            </section>
          ))}
        </div>
      )}

      <Modal open={uploadOpen} onClose={() => setUploadOpen(false)} title="文書を追加">
        <div className="space-y-3">
          <label className="block">
            <span className="text-caption text-muted">タイトル<span className="text-alert-text ml-1">*</span></span>
            <input className="mt-1 w-full min-h-[44px] border border-surface-border rounded px-3"
              value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </label>
          <label className="block">
            <span className="text-caption text-muted">説明</span>
            <textarea className="mt-1 w-full border border-surface-border rounded px-3 py-2" rows={2}
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </label>
          <CategorySelectField categoryList={categoryList} value={form.categoryId}
            onChange={(v) => setForm({ ...form, categoryId: v })} />
          <label className="block">
            <span className="text-caption text-muted">並び順 (小さいほど先頭)</span>
            <input type="number" className="mt-1 w-full min-h-[44px] border border-surface-border rounded px-3"
              value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: e.target.value })} />
          </label>
          <VisibilityField supported={supportsConditions} value={form.conditionSetId}
            onChange={(v) => setForm({ ...form, conditionSetId: v })}
            options={conditionSetOptions} />
          <div>
            <label className="flex items-start gap-2 min-h-[44px] py-2">
              <input type="checkbox" className="mt-1 w-5 h-5" checked={form.downloadAllowed}
                onChange={(e) => setForm({ ...form, downloadAllowed: e.target.checked })} />
              <span className="text-body">ダウンロードを許可する</span>
            </label>
            {/* ⚠️ design-expert H-2 / D-433: 誤った安心が生じるのは**チェックを外す管理者**の側。
                限界は閲覧側だけでなくここにも明示する。 */}
            <p className="text-body text-text-sub">
              許可しない場合もダウンロード用のボタンを出さないだけで、
              ブラウザの PDF ビューアの保存機能やスクリーンショットは防げません。
            </p>
          </div>
          <FilePickerField label="ファイル" required accept={DOCUMENT_ACCEPT}
            hint={DOCUMENT_FORMAT_HINT} onChange={setPickedFile} />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setUploadOpen(false)}>キャンセル</Button>
            <Button onClick={() => upload.mutate()} disabled={upload.isPending || !form.title || !pickedFile}>
              {upload.isPending ? '登録中…' : '登録'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={editing !== null} onClose={() => setEditing(null)} title="文書を編集">
        <div className="space-y-3">
          <label className="block">
            <span className="text-caption text-muted">タイトル<span className="text-alert-text ml-1">*</span></span>
            <input className="mt-1 w-full min-h-[44px] border border-surface-border rounded px-3"
              value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </label>
          <label className="block">
            <span className="text-caption text-muted">説明</span>
            <textarea className="mt-1 w-full border border-surface-border rounded px-3 py-2" rows={2}
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </label>
          <CategorySelectField categoryList={categoryList} value={form.categoryId}
            onChange={(v) => setForm({ ...form, categoryId: v })} />
          <label className="block">
            <span className="text-caption text-muted">並び順 (小さいほど先頭)</span>
            <input type="number" className="mt-1 w-full min-h-[44px] border border-surface-border rounded px-3"
              value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: e.target.value })} />
          </label>
          <VisibilityField supported={supportsConditions} value={form.conditionSetId}
            onChange={(v) => setForm({ ...form, conditionSetId: v })}
            options={conditionSetOptions}
            status={form.conditionSetId === editing?.visibility_condition_set_id ? editingStatus : undefined} />
          <div>
            <label className="flex items-start gap-2 min-h-[44px] py-2">
              <input type="checkbox" className="mt-1 w-5 h-5" checked={form.downloadAllowed}
                onChange={(e) => setForm({ ...form, downloadAllowed: e.target.checked })} />
              <span className="text-body">ダウンロードを許可する</span>
            </label>
            <p className="text-body text-text-sub">
              許可しない場合もダウンロード用のボタンを出さないだけで、
              ブラウザの PDF ビューアの保存機能やスクリーンショットは防げません。
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setEditing(null)}>キャンセル</Button>
            <Button onClick={() => editing && update.mutate(editing)} disabled={update.isPending || !form.title}>保存</Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        title="文書をアーカイブしますか？"
        message={`「${deleting?.title ?? ''}」は一覧から見えなくなります。過去の版と閲覧記録は保持されます。`}
        confirmLabel="アーカイブ"
        destructive
        loading={archive.isPending}
        onConfirm={() => deleting && archive.mutate(deleting)}
        onClose={() => setDeleting(null)}
      />

      <CategoryManagerModal
        open={categoryManagerOpen}
        onClose={() => setCategoryManagerOpen(false)}
        categoryList={categoryList}
        canManage={canManage}
        onRequestDelete={setDeletingCategory}
      />
      <ConfirmDialog
        open={deletingCategory !== null}
        title="カテゴリを削除しますか？"
        message={`「${deletingCategory?.name ?? ''}」を削除します。サブカテゴリや文書がこのカテゴリを使用している場合は削除できません。`}
        confirmLabel="削除"
        destructive
        loading={removeCategory.isPending}
        onConfirm={() => deletingCategory && removeCategory.mutate(deletingCategory)}
        onClose={() => setDeletingCategory(null)}
      />
    </div>
  );
}
